/** Copy and formatting for Publish and update — publish, schedule, and cancel flows. */

const SESSION_PREFIX = 'cp-pos-scheduled-publish-';

export interface ScheduledPublishPayload {
  scheduledAt: Date;
  schoolCount: number;
  /** Present for new schedules; required to cancel via data-ingest disable. */
  scheduleId?: string;
}

/** After scheduling from the drawer; always includes `scheduleId` for data-ingest cancel. */
export interface SchedulePublishSuccessPayload {
  planId: string;
  planName: string;
  scheduledAt: Date;
  schoolCount: number;
  scheduleId: string;
}

export function formatScheduledPublishLine(scheduledAt: Date): string {
  return scheduledAt.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Error toast when the user tries to publish or schedule with no schools selected (matches CPS validation). */
export function buildNoSchoolsSelectedForPublishToast(): {
  heading: string;
  subheading: string;
} {
  return {
    heading: 'Select at least one school',
    subheading: 'Choose one or more schools to publish this plan before continuing.',
  };
}

/** Info toast (heading + subheading) after immediate publish from Publish and update; async work continues in BE. */
export function buildPublishImmediateSuccessToastMessage(planName: string): {
  heading: string;
  subheading: string;
} {
  return {
    heading: `Publish and update in progress for ${planName}`,
    subheading: "You'll receive an email when complete.",
  };
}

/** Success toast after scheduling a publish (short heading + detail in subheading). */
export function buildSchedulePublishSuccessToastMessage(
  planName: string,
  schoolCount: number,
  scheduledAt: Date
): { heading: string; subheading: string } {
  const n = Math.max(0, schoolCount);
  const schoolWord = n === 1 ? 'school' : 'schools';
  const when = formatScheduledPublishLine(scheduledAt);
  return {
    heading: 'Publish scheduled',
    subheading: `${planName} will publish to ${n} ${schoolWord} on ${when}.`,
  };
}

/** Bold banner title (scheduled publication notice). */
export const SCHEDULED_PUBLISH_BANNER_TITLE = 'Publication scheduled';

/**
 * Body copy: date/time of the scheduled publication (not bold in UI).
 * Example: "This plan of study will be published on May 7, 2026 at 3:00 PM."
 */
export function buildScheduledPublishBannerPublicationSentence(scheduledAt: Date): string {
  const datePart = scheduledAt.toLocaleDateString(undefined, { dateStyle: 'medium' });
  const timePart = scheduledAt.toLocaleTimeString(undefined, { timeStyle: 'short' });
  return `This plan of study will be published on ${datePart} at ${timePart}.`;
}

/** Lock explanation below the date line (American spelling “canceled”). */
export const SCHEDULED_PUBLISH_BANNER_LOCK_COPY =
  'The plan is locked for editing until it is published or the scheduled publication is canceled.';

/**
 * True when staff must not use edit entry points for this plan (scheduled publish flag from CPS
 * and/or session after scheduling in this tab).
 */
export function isPlanScheduleLockedForStaffUi(
  publishScheduleFeatureEnabled: boolean,
  planId: string,
  checkedToPublishFromApi: boolean | undefined
): boolean {
  if (!publishScheduleFeatureEnabled) {
    return false;
  }
  if (checkedToPublishFromApi) {
    return true;
  }
  return readScheduledPublishFromSession(planId) !== null;
}

export function readScheduledPublishFromSession(planId: string): ScheduledPublishPayload | null {
  try {
    const raw = sessionStorage.getItem(SESSION_PREFIX + planId);
    if (!raw) {
      return null;
    }
    const p = JSON.parse(raw) as { at: string; schoolCount: number; scheduleId?: string };
    const scheduledAt = new Date(p.at);
    if (Number.isNaN(scheduledAt.getTime())) {
      return null;
    }
    if (scheduledAt.getTime() <= Date.now()) {
      sessionStorage.removeItem(SESSION_PREFIX + planId);
      return null;
    }
    const scheduleId =
      typeof p.scheduleId === 'string' && p.scheduleId.trim() !== ''
        ? p.scheduleId.trim()
        : undefined;
    return {
      scheduledAt,
      schoolCount: typeof p.schoolCount === 'number' ? p.schoolCount : 0,
      ...(scheduleId ? { scheduleId } : {}),
    };
  } catch {
    return null;
  }
}

export function writeScheduledPublishToSession(
  planId: string,
  payload: ScheduledPublishPayload
): void {
  try {
    sessionStorage.setItem(
      SESSION_PREFIX + planId,
      JSON.stringify({
        at: payload.scheduledAt.toISOString(),
        schoolCount: payload.schoolCount,
        ...(typeof payload.scheduleId === 'string' && payload.scheduleId.trim() !== ''
          ? { scheduleId: payload.scheduleId.trim() }
          : {}),
      })
    );
  } catch {
    /* quota / private mode */
  }
}

export function clearScheduledPublishSession(planId: string): void {
  try {
    sessionStorage.removeItem(SESSION_PREFIX + planId);
  } catch {
    /* ignore */
  }
}
